import {
  BucketMovementStatus,
  BucketMovementType,
  type BucketLocationType,
  type BucketMovement,
} from '@/integrations/agreements/client';
import { BUCKET_LOCATION_LABELS } from '@/utils/bucketRequestRules';
import { PERIOD_LABELS, formatDay } from '@/utils/onDemandRequestRules';
import { describeBucketQuantities } from './bucketQuantities';

/**
 * O resumo de uma solicitação para a linha da lista: o pedido como o gerador fez ("4 retiradas"),
 * não como o serviço grava (uma operação por visita).
 */

/** "4 retiradas", "1 colocação e 1 retirada". */
export function describeRequestOperations(movements: BucketMovement[]): string {
  const count = (type: BucketMovementType) => movements.filter((movement) => movement.type === type).length;

  return describeBucketQuantities({
    placement: count(BucketMovementType.PLACEMENT),
    removal: count(BucketMovementType.REMOVAL),
    swap: count(BucketMovementType.SWAP),
  });
}

const STATUS_ORDER = [
  BucketMovementStatus.PENDING_HAULER,
  BucketMovementStatus.PLANNED,
  BucketMovementStatus.DONE,
  BucketMovementStatus.BLOCKED,
  BucketMovementStatus.CANCELLED,
];

/** Quantas operações em cada status, na ordem do ciclo — só os status presentes. */
export function countRequestStatuses(movements: BucketMovement[]): { status: BucketMovementStatus; count: number }[] {
  return STATUS_ORDER
    .map((status) => ({ status, count: movements.filter((movement) => movement.status === status).length }))
    .filter(({ count }) => count > 0);
}

/** "05/10/2026", ou "05/10/2026 a 08/10/2026" quando as operações caem em dias diferentes. */
export function describeRequestDates(movements: BucketMovement[]): string {
  const days = [...new Set(movements.map((movement) => movement.plannedDate).filter(Boolean))].sort() as string[];

  if (days.length <= 1) return formatDay(days[0]);
  return `${formatDay(days[0])} a ${formatDay(days[days.length - 1])}`;
}

export function describeRequestPeriod(movements: BucketMovement[]): string {
  const periods = new Set(movements.map((movement) => movement.period));

  if (periods.size > 1) return 'Turnos diferentes';
  const [period] = periods;
  return period != null ? PERIOD_LABELS[period] : 'Sem preferência de turno';
}

/** O local, quando todas dizem o mesmo. A retirada não tem local: herda o da caçamba que sai. */
export function describeRequestLocation(movements: BucketMovement[]): string | null {
  const locations = new Set(movements.map((movement) => movement.locationType).filter((location): location is BucketLocationType => location != null));

  if (locations.size !== 1) return null;
  const [location] = locations;
  return BUCKET_LOCATION_LABELS[location];
}

/** Os números do que aconteceu; na operação ainda não registrada, o que o gerador informou ao pedir. */
export function requestBucketNumbers(movements: BucketMovement[]): string[] {
  return [...new Set(movements.flatMap((movement) => (
    movement.bucketNumbers.length
      ? movement.bucketNumbers
      : movement.requestedBucketNumber ? [movement.requestedBucketNumber] : []
  )))];
}
